/**
 * LinkManager Service
 *
 * Manages entity linking and relationship tracking.
 * Provides high-level API over the linksStore for cross-linking entities.
 *
 * Key Features:
 * - Create bidirectional links between entities
 * - Query linked entities by action type
 * - Get counts for fast action icon coloring
 * - Bulk operations for cleanup
 */

import { queryClient, queryKeys } from '@business-assistant/shared';
import { graphqlFetch } from '@business-assistant/shared';
import type { ActionType } from '../../types/row.types';
import type { LinkRole } from '@business-assistant/shared';
import { getPrefix } from '@business-assistant/shared/domain';

// GraphQL mutations for link operations
const CREATE_LINK_MUTATION = `
  mutation CreateEntityLink($input: EntityLinkInput!) {
    createEntityLink(input: $input) {
      id
      fromId
      toId
      fromRole
      toRole
      createdAt
    }
  }
`;

const DELETE_LINK_BETWEEN_MUTATION = `
  mutation DeleteLinkBetween($fromId: String!, $toId: String!) {
    deleteLinkBetween(fromId: $fromId, toId: $toId)
  }
`;

const DELETE_LINKS_FOR_MUTATION = `
  mutation DeleteLinksFor($entityId: String!) {
    deleteLinksFor(entityId: $entityId)
  }
`;

// =============================================================================
// LINK MANAGEMENT
// =============================================================================

/**
 * LinkManager - Static utility class for managing entity links
 */
export class LinkManager {
  /**
   * Creates a bidirectional link between two entities
   *
   * @param fromId - Source entity ID
   * @param toId - Target entity ID
   * @param role - Optional relationship role
   * @returns Link ID
   *
   * @example
   * ```tsx
   * // Link task to email
   * LinkManager.createLink('tsk-123', 'eml-456');
   *
   * // Link with specific role
   * LinkManager.createLink('tsk-123', 'cnt-789', 'related');
   * ```
   */
  static async createLink(fromId: string, toId: string, role?: LinkRole): Promise<string> {
    const input = { from_id: fromId, to_id: toId, from_role: role || null, to_role: null, metadata: null };
    const response = await graphqlFetch<{ createEntityLink: any }>(
      CREATE_LINK_MUTATION,
      { input }
    );
    if (response.errors) {
      throw new Error(response.errors.map((e) => e.message).join(', '));
    }
    const linkId = response.data?.createEntityLink?.id;

    queryClient.invalidateQueries({ queryKey: queryKeys.links.all() });

    return linkId;
  }

  /**
   * Deletes a link between two entities
   *
   * @param fromId - Source entity ID
   * @param toId - Target entity ID
   * @returns true if link was deleted
   */
  static async deleteLink(fromId: string, toId: string): Promise<boolean> {
    const response = await graphqlFetch<{ deleteLinkBetween: boolean }>(
      DELETE_LINK_BETWEEN_MUTATION,
      { fromId, toId }
    );
    if (response.errors) {
      throw new Error(response.errors.map((e) => e.message).join(', '));
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.links.all() });

    return response.data?.deleteLinkBetween ?? false;
  }

  /**
   * Gets all links for an entity
   *
   * @param entityId - Entity ID
   * @returns Array of link IDs
   */
  static getLinks(entityId: string): string[] {
    const links = queryClient.getQueryData<any[]>(queryKeys.links.all()) || [];
    return links.filter((link: any) => link.fromId === entityId || link.toId === entityId).map((link: any) => link.id);
  }

  /**
   * Gets all linked entity IDs for an entity
   *
   * @param entityId - Entity ID
   * @returns Array of linked entity IDs
   */
  static getLinkedIds(entityId: string): string[] {
    const links = queryClient.getQueryData<any[]>(queryKeys.links.all()) || [];
    const linkedIds: string[] = [];
    for (const link of links) {
      if (link.fromId === entityId) linkedIds.push(link.toId);
      if (link.toId === entityId) linkedIds.push(link.fromId);
    }
    return linkedIds;
  }

  /**
   * Checks if two entities are linked
   *
   * @param id1 - First entity ID
   * @param id2 - Second entity ID
   * @returns true if entities are linked
   */
  static hasLink(id1: string, id2: string): boolean {
    const links = queryClient.getQueryData<any[]>(queryKeys.links.all()) || [];
    return links.some((link: any) =>
      (link.fromId === id1 && link.toId === id2) ||
      (link.fromId === id2 && link.toId === id1)
    );
  }

  /**
   * Deletes all links for an entity (cleanup on entity deletion)
   *
   * @param entityId - Entity ID
   */
  static async deleteLinksFor(entityId: string): Promise<void> {
    const response = await graphqlFetch<{ deleteLinksFor: number }>(
      DELETE_LINKS_FOR_MUTATION,
      { entityId }
    );
    if (response.errors) {
      throw new Error(response.errors.map((e) => e.message).join(', '));
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.links.all() });
  }

  // ===========================================================================
  // FAST ACTIONS INTEGRATION
  // ===========================================================================

  /**
   * Gets count of linked entities per action type
   * Used for icon coloring in fast actions
   *
   * @param entityId - Entity ID
   * @returns Record mapping action types to link counts
   *
   * @example
   * ```tsx
   * const counts = LinkManager.getLinkedActionCounts('tsk-123');
   * // => { call: 2, email: 1, message: 0, ... }
   *
   * // Use for icon coloring
   * const isLinked = counts.email > 0; // true
   * ```
   */
  static getLinkedActionCounts(entityId: string): Record<ActionType, number> {
    const linkedIds = this.getLinkedIds(entityId);

    return {
      call: linkedIds.filter((id) => id.startsWith('cal-')).length,
      videocall: linkedIds.filter((id) => id.startsWith('vcl-')).length,
      email: linkedIds.filter((id) => id.startsWith('eml-')).length,
      message: linkedIds.filter((id) => id.startsWith('msg-')).length,
      task: linkedIds.filter((id) => id.startsWith('tsk-')).length,
      event: linkedIds.filter((id) => id.startsWith('cal-')).length, // Using call prefix
      file: linkedIds.filter((id) => id.startsWith('fil-')).length,
      invoice: linkedIds.filter((id) => id.startsWith('inv-')).length,
      note: linkedIds.filter((id) => id.startsWith('not-')).length,
    };
  }

  /**
   * Gets linked entities of a specific type
   *
   * @param entityId - Source entity ID
   * @param prefix - Target entity prefix (e.g., 'eml', 'cal')
   * @returns Array of linked entity IDs of specified type
   *
   * @example
   * ```tsx
   * // Get all emails linked to a task
   * const emails = LinkManager.getLinkedByType('tsk-123', 'eml');
   * // => ['eml-456', 'eml-789']
   * ```
   */
  static getLinkedByType(entityId: string, prefix: string): string[] {
    const linkedIds = this.getLinkedIds(entityId);
    return linkedIds.filter((id) => id.startsWith(`${prefix}-`));
  }

  /**
   * Gets count of linked entities of a specific type
   *
   * @param entityId - Source entity ID
   * @param prefix - Target entity prefix
   * @returns Count of linked entities
   */
  static getLinkedCountByType(entityId: string, prefix: string): number {
    return this.getLinkedByType(entityId, prefix).length;
  }

  // ===========================================================================
  // BATCH OPERATIONS
  // ===========================================================================

  /**
   * Creates multiple links at once
   *
   * @param fromId - Source entity ID
   * @param toIds - Array of target entity IDs
   * @returns Array of created link IDs
   *
   * @example
   * ```tsx
   * // Link task to multiple contacts
   * LinkManager.createMultipleLinks('tsk-123', ['cnt-1', 'cnt-2', 'cnt-3']);
   * ```
   */
  static async createMultipleLinks(fromId: string, toIds: string[]): Promise<string[]> {
    const promises = toIds.map((toId) => this.createLink(fromId, toId));
    return await Promise.all(promises);
  }

  /**
   * Gets all entities linked to a set of source entities
   * Useful for bulk operations
   *
   * @param entityIds - Array of source entity IDs
   * @returns Array of unique linked entity IDs
   */
  static getLinkedIdsForMultiple(entityIds: string[]): string[] {
    const allLinked = entityIds.flatMap((id) => this.getLinkedIds(id));
    return Array.from(new Set(allLinked)); // Remove duplicates
  }

  // ===========================================================================
  // QUERY HELPERS
  // ===========================================================================

  /**
   * Gets entity type from ID
   *
   * @param entityId - Entity ID
   * @returns Entity type prefix or null
   */
  static getEntityType(entityId: string): string | null {
    return getPrefix(entityId);
  }

  /**
   * Filters entity IDs by type
   *
   * @param entityIds - Array of entity IDs
   * @param prefix - Entity prefix to filter by
   * @returns Filtered array
   *
   * @example
   * ```tsx
   * const allLinked = ['eml-1', 'tsk-2', 'eml-3', 'cnt-4'];
   * const emails = LinkManager.filterByType(allLinked, 'eml');
   * // => ['eml-1', 'eml-3']
   * ```
   */
  static filterByType(entityIds: string[], prefix: string): string[] {
    return entityIds.filter((id) => id.startsWith(`${prefix}-`));
  }

  /**
   * Groups linked entities by type
   *
   * @param entityId - Source entity ID
   * @returns Record mapping prefixes to entity IDs
   *
   * @example
   * ```tsx
   * const grouped = LinkManager.groupLinkedByType('tsk-123');
   * // => {
   * //   eml: ['eml-1', 'eml-2'],
   * //   cal: ['cal-3'],
   * //   cnt: ['cnt-4']
   * // }
   * ```
   */
  static groupLinkedByType(entityId: string): Record<string, string[]> {
    const linkedIds = this.getLinkedIds(entityId);
    const grouped: Record<string, string[]> = {};

    for (const id of linkedIds) {
      const prefix = id.split('-')[0];
      if (!grouped[prefix]) {
        grouped[prefix] = [];
      }
      grouped[prefix].push(id);
    }

    return grouped;
  }

  /**
   * Gets statistics about linked entities
   *
   * @param entityId - Entity ID
   * @returns Statistics object
   */
  static getLinkStatistics(entityId: string): {
    total: number;
    byType: Record<string, number>;
  } {
    const grouped = this.groupLinkedByType(entityId);
    const byType: Record<string, number> = {};

    for (const [prefix, ids] of Object.entries(grouped)) {
      byType[prefix] = ids.length;
    }

    return {
      total: this.getLinkedIds(entityId).length,
      byType,
    };
  }
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Gets linked action counts (convenience wrapper)
 */
export function getLinkedActionCounts(entityId: string): Record<ActionType, number> {
  return LinkManager.getLinkedActionCounts(entityId);
}

/**
 * Checks if action is linked (convenience wrapper)
 */
export function isActionLinked(entityId: string, actionType: ActionType): boolean {
  const counts = LinkManager.getLinkedActionCounts(entityId);
  return counts[actionType] > 0;
}
